import React, { useCallback, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import {
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tab,
  Typography,
} from '@material-ui/core';
import { getJSON, axiosInstance } from 'common/network';
import DecisionModal from './components/DecisionModal';

const useStyles = makeStyles(theme => ({
  root: { padding: theme.spacing(3) },
  content: { marginTop: theme.spacing(2) },
  empty: { padding: theme.spacing(4), textAlign: 'center' },
  answers: { whiteSpace: 'pre-line' },
  actions: { whiteSpace: 'nowrap' },
  spacer: { marginRight: theme.spacing(1) },
}));

const ROWS_PER_PAGE = 10;

// Pending first: this screen exists to be worked through, not browsed. The other two
// tabs are there to answer "what did we decide about this account", which is the second
// question anyone asks.
const TABS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'denied', label: 'Denied' },
];

const STATUS_COLOURS = {
  pending: 'default',
  approved: 'primary',
  denied: 'secondary',
};

const formatAnswers = (answers) =>
  Object.entries(answers || {})
    .map(([key, value]) => `${key.replace(/_/g, ' ')}: ${value}`)
    .join('\n');

const formatDate = (value) => (value ? new Date(value).toLocaleString() : '—');

const AccessRequestList = () => {
  const classes = useStyles();

  const [status, setStatus] = useState('pending');
  const [requests, setRequests] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [deciding, setDeciding] = useState(null);

  // Under /v3/accounts because that is what the load balancer routes to api-accounts.
  // A bare /v3/access_requests would fall through to the catch-all rule and land on a
  // different service.
  const load = useCallback(() => {
    const url = new URL(process.env.REACT_APP_API_ROOT + 'v3/accounts/access_requests');
    url.searchParams.append('status', status);
    url.searchParams.append('page', currentPage + 1);
    url.searchParams.append('page_size', ROWS_PER_PAGE);

    setLoading(true);
    getJSON(url)
      .then((response) => {
        setRequests(response?.access_requests ?? []);
        setTotalCount(response?.total_size ?? 0);
      })
      .finally(() => setLoading(false));
  }, [status, currentPage]);

  useEffect(() => { load(); }, [load]);

  const handleTabChange = (event, value) => {
    setStatus(value);
    setCurrentPage(0);
  };

  // axiosInstance rather than postJSON: postJSON resolves whatever the status was, so a
  // 409 (already decided) or a 500 would close the dialog and look like success. This
  // rejects on anything that is not 2xx, which is what the modal's error path expects.
  const submitDecision = (request, approved, note) => {
    const url =
      process.env.REACT_APP_API_ROOT + `v3/accounts/access_requests/${request.uuid}`;

    return axiosInstance.post(url, { approved, note }).then(() => {
      setDeciding(null);
      load();
    });
  };

  return (
    <div className={classes.root}>
      <Typography variant="h4">Access requests</Typography>
      <Typography variant="body2" color="textSecondary">
        Requests raised when someone registered through a specific intent. Approving one
        reopens self-service licence issuance for that account; the licence and any package
        are still granted from the account page.
      </Typography>

      <Card className={classes.content}>
        <Tabs value={status} onChange={handleTabChange} indicatorColor="primary" textColor="primary">
          {TABS.map((tab) => (
            <Tab key={tab.value} value={tab.value} label={tab.label} />
          ))}
        </Tabs>

        <CardContent>
          {loading && <div className={classes.empty}><CircularProgress /></div>}

          {!loading && requests.length === 0 && (
            <Typography className={classes.empty} color="textSecondary">
              {status === 'pending' ? 'Nothing waiting.' : 'Nothing here.'}
            </Typography>
          )}

          {!loading && requests.length > 0 && (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Account</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Intent</TableCell>
                    <TableCell>Answers</TableCell>
                    <TableCell>Submitted</TableCell>
                    {status === 'pending' ? (
                      <TableCell align="right">Decision</TableCell>
                    ) : (
                      <TableCell>Decided</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requests.map((request) => (
                    <TableRow hover key={request.uuid}>
                      <TableCell>
                        {request.account_uuid ? (
                          <Link href={`/accounts/${request.account_uuid}`}>
                            {request.account_name || '(no name)'}
                          </Link>
                        ) : (
                          request.account_name || '(no name)'
                        )}
                      </TableCell>
                      <TableCell>{request.account_email_address}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={request.intent}
                          color={STATUS_COLOURS[request.status] || 'default'}
                        />
                      </TableCell>
                      <TableCell className={classes.answers}>
                        {formatAnswers(request.answers) || '—'}
                      </TableCell>
                      <TableCell>{formatDate(request.created_at)}</TableCell>
                      {status === 'pending' ? (
                        <TableCell align="right" className={classes.actions}>
                          <Button
                            className={classes.spacer}
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={() => setDeciding({ request, approved: true })}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setDeciding({ request, approved: false })}
                          >
                            Deny
                          </Button>
                        </TableCell>
                      ) : (
                        <TableCell>
                          {formatDate(request.decided_at)}
                          {request.decision_note && (
                            <Typography variant="caption" display="block" color="textSecondary">
                              {request.decision_note}
                            </Typography>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <TablePagination
                component="div"
                count={totalCount}
                page={currentPage}
                rowsPerPage={ROWS_PER_PAGE}
                rowsPerPageOptions={[ROWS_PER_PAGE]}
                onChangePage={(event, page) => setCurrentPage(page)}
              />
            </>
          )}
        </CardContent>
      </Card>

      {deciding && (
        <DecisionModal
          request={deciding.request}
          approved={deciding.approved}
          onCancel={() => setDeciding(null)}
          onConfirm={(note) => submitDecision(deciding.request, deciding.approved, note)}
        />
      )}
    </div>
  );
};

export default AccessRequestList;
